import {
  isValid,
  addMilliseconds,
  addSeconds,
  addMinutes,
  addHours,
  addDays,
  addWeeks,
  addQuarters,
  addMonths,
  addYears,
  isAfter,
  formatDuration,
  format as dateFNSFormat,
} from "date-fns";
import { DEFAULT_LOCALE, EMPTY_STRING_LABEL } from "../core/constants/appGlobals";
import { DateWindow } from "../core/models/DateWindow";

export type addTimeValidUnits =
  | "milliseconds"
  | "millisecond"
  | "ms"
  | "seconds"
  | "second"
  | "s"
  | "minutes"
  | "minute"
  | "m"
  | "hours"
  | "hour"
  | "h"
  | "days"
  | "day"
  | "d"
  | "weeks"
  | "week"
  | "w"
  | "months"
  | "month"
  | "mo"
  | "quarters"
  | "quarter"
  | "q"
  | "years"
  | "year"
  | "y";

export default function addTime(date: Date, delta: number, unit: addTimeValidUnits) {
  switch (unit) {
    case "milliseconds":
    case "millisecond":
    case "ms":
      return addMilliseconds(date, delta);

    case "seconds":
    case "second":
    case "s":
      return addSeconds(date, delta);

    case "minutes":
    case "minute":
    case "m":
      return addMinutes(date, delta);

    case "hours":
    case "hour":
    case "h":
      return addHours(date, delta);

    case "days":
    case "day":
    case "d":
      return addDays(date, delta);

    case "weeks":
    case "week":
    case "w":
      return addWeeks(date, delta);

    case "months":
    case "month":
    case "mo":
      return addMonths(date, delta);

    case "quarters":
    case "quarter":
    case "q":
      return addQuarters(date, delta);

    case "years":
    case "year":
    case "y":
      return addYears(date, delta);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isDateValid(date: any) {
  if (date && isValid(new Date(date)) && isAfter(new Date(date), new Date(1, 1, 1))) {
    return true;
  }

  return false;
}

export type dateWindowBuildOptions = "2d" | "1w" | "2w" | "1M" | "2M" | "3M" | "6M";

export function buildDateWindow(option: dateWindowBuildOptions): DateWindow {
  const now = new Date();

  let start = now;

  switch (option) {
    case "2d":
      start = addTime(now, -2, "d");
      break;
    case "1w":
      start = addTime(now, -7, "d");
      break;
    case "2w":
      start = addTime(now, -14, "d");
      break;
    case "1M":
      start = addTime(now, -1, "mo");
      break;
    case "2M":
      start = addTime(now, -2, "mo");
      break;
    case "3M":
      start = addTime(now, -3, "mo");
      break;
    case "6M":
      start = addTime(now, -6, "mo");
      break;
    default:
      throw new Error("invalid date window option!");
  }

  return { start, end: now };
}

interface formatDurationOptions {
  format?: ["years", "months", "weeks", "days", "hours", "minutes", "seconds"];
  zero?: boolean;
  delimiter?: string;
  locale?: Locale;
}

export function localeFormatDuration(duration: Duration, options?: formatDurationOptions): string {
  return formatDuration(duration, {
    format: ["years", "months", "weeks", "days", "hours", "minutes"],
    locale: DEFAULT_LOCALE,
    ...options,
  });
}

export const formatDateTime = (date?: Date | string, format?: string): string => {
  if (!isDateValid(date)) {
    return EMPTY_STRING_LABEL;
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return dateFNSFormat(new Date(date!), format || "dd/MM/yyyy HH:mm");
};

export function format(date: number | Date, formatStr: string) {
  return dateFNSFormat(date, formatStr, { locale: DEFAULT_LOCALE });
}
